import {
    createClusterFilters,
    createHealthStatFilters,
    createPresetOnChangeHandler,
    findAppliedFilters,
} from 'utils/presets';
import styled, { useTheme } from 'styled-components';
import FilterCheckboxChip from './FilterCheckboxChip';
import { GOOGLE_SORT_OPTIONS } from 'utils/metrics';
import PresetTooltip from '../PresetTooltip';
import { withMixpanelChange } from 'utils/app';

const risingOrFlatClusters = createClusterFilters([
    'sustained riser',
    'flat',
    'seasonal riser',
]);

const risingClusters = createClusterFilters([
    'sustained riser',
    'seasonal riser',
    'rising star',
]);

const sortOptionMoMIncreaseDescending = GOOGLE_SORT_OPTIONS.MoM_increase;
const sortOptionYoYIncreaseDescending = GOOGLE_SORT_OPTIONS.absoluteGrowth;

const presets = {
    trendingThisMonth: {
        value: 'trendingThisMonth',
        label: 'Trending This Month',
        definition:
            'Catch emerging search intent before it hits the mass market.',
        filters: {
            cluster: risingOrFlatClusters,
        },
        sortOption: sortOptionMoMIncreaseDescending,
    },
    trendingThisYear: {
        value: 'trendingThisYear',
        label: 'Trending This Year',
        definition:
            'Optimize for high-volume category pillars that are still gaining market share.',
        filters: {
            cluster: risingOrFlatClusters,
        },
        sortOption: sortOptionYoYIncreaseDescending,
    },
    whiteSpaceOpportunity: {
        value: 'whiteSpaceOpportunity',
        label: 'Whitespace Opportunity',
        definition:
            'Identify high-intent consumer needs with low brand saturation.',
        filters: {
            cluster: risingClusters,
            healthStats: createHealthStatFilters([
                ['average brand searches', [4, 6, 8]],
                ['average monthly searches', [2]],
            ]),
        },
        sortOption: sortOptionYoYIncreaseDescending,
    },
};

const PresetFilterCheckboxChip = withMixpanelChange(
    ({ value, mixpanelContext = 'PresetFilterSelect', ...props }) => {
        return (
            <PresetTooltip preset={value} presets={presets}>
                <FilterCheckboxChip {...props} />
            </PresetTooltip>
        );
    },
    'PresetFilterCheckboxChip',
);

const Wrapper = styled.div`
    display: inline-flex;
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.space_6}px;
`;

function GooglePresetFilterChips({
    children,
    filters,
    setFilters,
    sortOption,
    setSortOption,
    initialSortOption,
    healthStatOptions,
    ...props
}) {
    const theme = useTheme();
    const appliedFilters = findAppliedFilters({ filters, presets, sortOption });
    const onChange = createPresetOnChangeHandler({
        filters,
        setFilters,
        sortOption,
        setSortOption,
        initialSortOption,
        theme,
        presets,
    });
    return (
        <Wrapper {...props}>
            {children}
            {Object.values(presets)
                .filter(({ filters }) => {
                    return (
                        !filters.healthStats ||
                        filters.healthStats.every(({ metricLabel }) =>
                            healthStatOptions.some(
                                ({ label }) => label === metricLabel,
                            ),
                        )
                    );
                })
                .map((option) => {
                    const { label, value } = option;
                    const checked = !!appliedFilters?.find(
                        (appliedFilter) => appliedFilter?.value === value,
                    );

                    return (
                        <PresetFilterCheckboxChip
                            value={value}
                            checked={checked}
                            onChange={(nextChecked) =>
                                onChange({
                                    action: nextChecked
                                        ? 'select-option'
                                        : 'remove-value',
                                    option,
                                })
                            }
                            handleMixpanelEvent={(nextChecked) => ({
                                action: nextChecked
                                    ? 'select-option'
                                    : 'remove-value',
                                label: option.label,
                                value: option.value,
                            })}
                            preserveIconSpace={false}
                            key={label}
                        >
                            <span>{label}</span>
                        </PresetFilterCheckboxChip>
                    );
                })}
        </Wrapper>
    );
}

export default GooglePresetFilterChips;
